<div class="content-container margin-bottom">
  <section role="region" aria-label="Department controls" class="controls">
    <h2 class="visually-hidden">Department controls</h2>
    <div class="left-controls">
      <div class="filters">
        <app-filter-editor
          [formControl]="filterControl"
          [chips]="['name', 'key', 'member', 'member.hawkid', 'effort']"
          label="Filter departments - search by attribute or type freely to search all."
          [selectedFilters]="(selectedFilters | async) || []"
          [chipPossVals]="chipPossVals" [chipHelpers]='chipHelpers'
          (filterChange)="filterChange($event)"
          class="filter-editor">
        </app-filter-editor>

        <button
          mat-stroked-button
          (click)="selectedFilters.next([])">
          <mat-icon class="material-symbols-outlined">clear</mat-icon>
          Clear
        </button>
      </div>
    </div>

    <div class="right-controls">
      <button
        mat-stroked-button
        (click)="openEditor(null, true)">
        <mat-icon>add</mat-icon>
        Add New Department
      </button>
    </div>
  </section>
</div>

<div class="working-with margin-sm-bottom margin-sm-left">
  @if (effort) {
    <div class="mode-helper arrow-box">
      <button
        mat-icon-button
        (click)='clearFilter("effort")'
        title="Remove effort filter from filters.">
        <mat-icon>cancel</mat-icon>
      </button>
      Working with Effort:<b>'{{effort.title}}'</b>
    </div>
  }

  @if (member) {
    <div class="mode-helper arrow-box">
      <button
        mat-icon-button
        (click)='clearFilter("member.hawkid", "member")'
        title="Remove member filter from filters.">
        <mat-icon>cancel</mat-icon>
      </button>
      <i>Working with member:<b>'{{member.displayName}}'</b></i>
    </div>
  }
</div>

<section role="region" aria-label="Departments table entries" class='group-table-container'>
  <h2 class="visually-hidden">Department table entries</h2>
  <table
    mat-table
    [dataSource]="dataSource"
    class="group-table"
    multiTemplateDataRows
    matSort
    matSortDirection="asc"
    matSortActive="name">

    <ng-container matColumnDef="name">
      <th mat-header-cell *matHeaderCellDef mat-sort-header scope="col">Department Name</th>
      <td mat-cell *matCellDef="let group" (click)="toggleExpand(group.id)">
        <a href="javascript:void(0)" (click)="openEditor(group, false)"
        title="Edit this department.">{{ group.name }}</a>
      </td>
    </ng-container>

    <ng-container matColumnDef="owners">
      <th mat-header-cell *matHeaderCellDef scope="col">Owner(s)</th>
      <td mat-cell *matCellDef="let group">
        <a [routerLink]="['/users']" [queryParams]="{ 'filter.dept.key': group?.key }"
          title="View users associated with this group.">
          {{ groupMembersSummary(group) }}
        </a>
      </td>
    </ng-container>

    <ng-container matColumnDef="efforts">
      <th mat-header-cell *matHeaderCellDef scope="col">Efforts</th>
      <td mat-cell *matCellDef="let group">
        <a [routerLink]="['/efforts']" [queryParams]="{ 'filter.dept.key': group?.key }"
          title="View efforts associated with this department.">
          {{ ! group.efforts?.length ? "No" : group.efforts.length }} efforts
        </a>
      </td>
    </ng-container>

    <ng-container matColumnDef="tools">
      <th mat-header-cell *matHeaderCellDef scope="col">Actions</th>
      <td mat-cell *matCellDef="let group">
        <button
          mat-icon-button
          (click)="deleteGroup(group)"
          title="Delete this group along with all its memberships.">
          <mat-icon>delete</mat-icon>
        </button>
      </td>
    </ng-container>

    <!-- header no results message -->
    <ng-container matColumnDef='no-results'>
      <td mat-header-cell *matHeaderCellDef [attr.colspan]='4' class='whole-row-no-results whole-row-helper'>
        @if (! (isLoading | async)) {
          <div>
            <p>No groups found
              @if ((selectedFilters | async)?.length) {
                <button mat-flat-button color="accent"
                  (click)='selectedFilters.next([])'>Clear
                Filter</button>
              }
              <button mat-stroked-button (click)="openEditor(null, true)">
                <mat-icon>add</mat-icon>
                Add New Department
              </button>
            </p>
          </div>
        }
        <ng-template #waiting>
          Fetching data
        </ng-template>
      </td>
    </ng-container>

    <!-- Unused Columns -->
    <ng-container matColumnDef="effort-specific-tools">
      <th mat-header-cell *matHeaderCellDef class='specific-tools' scope="col">Effort Actions</th>
      <td mat-cell *matCellDef="let group" class="specific-tools">
        @if (effort) {
          <div class="row-tools">
            <button mat-icon-button (click)="removeGroupFromEffort(group, effort)"
              title="Remove group from active effort.">
              <mat-icon>group_remove</mat-icon>
            </button>
          </div>
        }
      </td>
    </ng-container>
    <ng-container matColumnDef="member-specific-tools">
      <th mat-header-cell *matHeaderCellDef class='specific-tools' scope="col">Member Actions</th>
      <td mat-cell *matCellDef="let group" class="specific-tools">
        @if (member) {
          <div class="row-tools">
            <button mat-icon-button (click)="removeMemberFromGroup(member, group)"
              title="Remove active person from group.">
              <mat-icon>person_remove</mat-icon>
            </button>
          </div>
        }
      </td>
    </ng-container>
    <ng-container matColumnDef="tags">
      <th mat-header-cell *matHeaderCellDef scope="col">Tags</th>
      <td mat-cell *matCellDef="let group">
        <mat-chip-set>
          @for (tag of group.tags; track tag) {
            <mat-chip>
              {{ tag }}
              <!-- <mat-icon matChipRemove>cancel</mat-icon> -->
            </mat-chip>
          }
        </mat-chip-set>
      </td>
    </ng-container>
    <ng-container matColumnDef="key">
      <th mat-header-cell *matHeaderCellDef mat-sort-header scope="col">Key</th>
      <td mat-cell *matCellDef="let group" (click)="toggleExpand(group.id)">
        {{ group.key }}
      </td>
    </ng-container>
    <!-- /End Unused Columns -->

    <tr
      mat-header-row
      *matHeaderRowDef="activeColumns; sticky: true">
    </tr>
    <tr
      mat-row
            *matRowDef="let row; 
            columns: activeColumns;">
    </tr>
    <tr
      [hidden]='dataSource.data.length > 0'
      mat-header-row
      *matHeaderRowDef="['no-results'];"
      class="no-results">
    </tr>
  </table>

  <div class="loading-data" [class.showing]="isLoading | async">
    <mat-spinner></mat-spinner>
  </div>
</section>

<section role="region" aria-label="Departments pagination" class="bottom-tools">
  <h2 class="visually-hidden">Department table pagination</h2>
  <mat-paginator
    [pageSizeOptions]="[10, 15, 20, 30, 40]"
    showFirstLastButton
    pageSize='10'
    [length]="totalDepts">
  </mat-paginator>
  <div>
    @if (effort) {
      <button mat-stroked-button color="primary" class="specific-action"
        (click)="openAddToEffortDialog()"><mat-icon>add</mat-icon> Add more groups to '{{effort.title}}'</button>
      }
    @if (member) {
      <button mat-stroked-button color="primary" class="specific-action"
        (click)="openAddGroupsToMember()"><mat-icon>add</mat-icon> Add '{{member.hawkId}}' to groups</button>
      }
  </div>
</section>